import { useEffect, useState, type FormEvent } from "react";
import { api } from "../lib/api";
import { useAuth } from "../app/AuthContext";
import { safeHttpUrl } from "../lib/safeUrl";

interface SourceRow {
  id: string;
  name: string;
  url: string;
  source_type: string;
  scan_frequency: string;
  status: string;
  quality_score: number;
  last_scanned_at: string | null;
  search_query: string | null;
  search_region: string | null;
  search_last_run_at: string | null;
  search_results_total: number | null;
}

type DryRunResult = Awaited<ReturnType<typeof api.admin.sourceDryRun>>;

const SOURCE_TYPES = [
  "blog", "stockist", "newsletter", "magazine", "directory",
  "search_query", "shopify_index", "social_account", "manual",
];

const FREQUENCIES = ["hourly", "every_6h", "daily", "weekly", "monthly"];

export function AdminSourcesPage() {
  const { user } = useAuth();
  const [items, setItems] = useState<SourceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState({
    name: "",
    url: "",
    sourceType: "search_query",
    scanFrequency: "daily",
    qualityScore: 50,
    query: "",
    region: "us",
  });
  const [editing, setEditing] = useState<SourceRow | null>(null);
  const [runs, setRuns] = useState<Awaited<ReturnType<typeof api.admin.sourceRuns>>["items"] | null>(null);
  const [dryRun, setDryRun] = useState<DryRunResult | null>(null);
  const [dryRunLoading, setDryRunLoading] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    api.admin.sourcesList().then((r) => setItems(r.items)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  if (!user || (user.role !== "admin" && user.role !== "super_admin")) {
    return <div className="container empty-state">Admin access required.</div>;
  }

  const onCreate = async (e: FormEvent) => {
    e.preventDefault();
    if (!creating.name) return;
    if (creating.sourceType === "search_query" && !creating.query) return;
    if (creating.sourceType !== "search_query" && !creating.url) return;
    await api.admin.sourceCreate(creating);
    setCreating({ ...creating, name: "", url: "", query: "" });
    load();
  };

  const onUpdate = async (s: SourceRow) => {
    await api.admin.sourceUpdate(s.id, {
      name: s.name, url: s.url, source_type: s.source_type,
      scan_frequency: s.scan_frequency, status: s.status, quality_score: s.quality_score,
      search_query: s.search_query, search_region: s.search_region,
    });
    setEditing(null);
    load();
  };

  const onScan = async (id: string) => {
    await api.admin.sourceScan(id);
    alert("Scan queued. Check Runs to see progress.");
  };

  const onShowRuns = async (id: string) => {
    const r = await api.admin.sourceRuns(id);
    setRuns(r.items);
  };

  const onDryRun = async (id: string) => {
    setDryRunLoading(id);
    try {
      const r = await api.admin.sourceDryRun(id, 10);
      setDryRun(r);
    } finally {
      setDryRunLoading(null);
    }
  };

  return (
    <div className="container" style={{ paddingBlock: "2rem", maxWidth: 1200 }}>
      <h2>Sources</h2>

      <section className="admin-discovery-guide" aria-label="How discovery finds new brands">
        <div>
          <h3>How new brands are found</h3>
          <p>
            Add a search phrase, stockist, directory or editorial source. Scans extract brand-like
            homepages, skip major platforms, dedupe known domains, then queue new candidates for extraction and scoring.
          </p>
        </div>
        <div className="admin-discovery-guide__steps">
          <span>1. Source</span>
          <span>2. Scan</span>
          <span>3. Candidate</span>
          <span>4. Review or publish</span>
        </div>
      </section>

      <form onSubmit={onCreate} className="filter-bar" style={{ marginTop: "1.5rem", flexWrap: "wrap" }}>
        <input placeholder="Source name" value={creating.name} onChange={(e) => setCreating({ ...creating, name: e.target.value })} style={{ maxWidth: 240 }} />
        {creating.sourceType === "search_query" ? (
          <>
            <input placeholder="Search phrase, e.g. emerging technical clothing brand" value={creating.query} onChange={(e) => setCreating({ ...creating, query: e.target.value })} style={{ maxWidth: 420 }} />
            <input placeholder="Region" value={creating.region} onChange={(e) => setCreating({ ...creating, region: e.target.value })} style={{ maxWidth: 100 }} />
          </>
        ) : (
          <input placeholder="https://…" value={creating.url} onChange={(e) => setCreating({ ...creating, url: e.target.value })} style={{ maxWidth: 320 }} />
        )}
        <select value={creating.sourceType} onChange={(e) => setCreating({ ...creating, sourceType: e.target.value })}>
          {SOURCE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={creating.scanFrequency} onChange={(e) => setCreating({ ...creating, scanFrequency: e.target.value })}>
          {FREQUENCIES.map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
        <input type="number" min={0} max={100} value={creating.qualityScore} onChange={(e) => setCreating({ ...creating, qualityScore: Number(e.target.value) })} style={{ maxWidth: 90 }} />
        <button type="submit" className="primary">+ Add discovery source</button>
      </form>

      <div className="discovery-presets" aria-label="Search query starter ideas">
        {[
          "emerging clothing brand",
          "new independent fashion brand",
          "technical clothing brand",
          "urban outdoor brand",
          "low logo menswear brand",
        ].map((query) => (
          <button
            type="button"
            key={query}
            onClick={() => setCreating({
              ...creating,
              sourceType: "search_query",
              name: `Query: ${query}`,
              query,
              url: "",
            })}
          >
            {query}
          </button>
        ))}
      </div>

      {loading ? <div className="empty-state">Loading…</div> : (
        <div className="admin-table-wrap"><table className="admin-table">
          <thead>
            <tr><th>Name</th><th>Target</th><th>Type</th><th>Frequency</th><th>Status</th><th>Quality</th><th>Last scan</th><th></th></tr>
          </thead>
          <tbody>
            {items.map((s) => {
              const e = editing?.id === s.id ? editing : null;
              return (
                <tr key={s.id}>
                  <td>{e
                    ? <input value={e.name} onChange={(ev) => setEditing({ ...e, name: ev.target.value })} />
                    : s.name}</td>
                  <td>{e
                    ? e.source_type === "search_query"
                      ? <div style={{ display: "grid", gap: "0.4rem", minWidth: 240 }}>
                          <input value={e.search_query ?? ""} onChange={(ev) => setEditing({ ...e, search_query: ev.target.value })} />
                          <input value={e.search_region ?? ""} placeholder="region" onChange={(ev) => setEditing({ ...e, search_region: ev.target.value })} />
                        </div>
                      : <input value={e.url} onChange={(ev) => setEditing({ ...e, url: ev.target.value })} />
                    : s.source_type === "search_query"
                      ? <div>
                          <div>{s.search_query ?? "No query configured"}</div>
                          <div className="admin-source-meta">
                            {s.search_region ? s.search_region.toUpperCase() : "GLOBAL"} · {s.search_results_total ?? 0} last results
                          </div>
                        </div>
                      : <SafeExternalLink url={s.url} label={shorten(s.url)} />}</td>
                  <td>{e
                    ? <select value={e.source_type} onChange={(ev) => setEditing({ ...e, source_type: ev.target.value })}>
                        {SOURCE_TYPES.map((t) => <option key={t}>{t}</option>)}
                      </select>
                    : s.source_type}</td>
                  <td>{e
                    ? <select value={e.scan_frequency} onChange={(ev) => setEditing({ ...e, scan_frequency: ev.target.value })}>
                        {FREQUENCIES.map((f) => <option key={f}>{f}</option>)}
                      </select>
                    : s.scan_frequency}</td>
                  <td>{e
                    ? <select value={e.status} onChange={(ev) => setEditing({ ...e, status: ev.target.value })}>
                        <option value="active">active</option><option value="paused">paused</option><option value="disabled">disabled</option>
                      </select>
                    : s.status}</td>
                  <td>{e
                    ? <input type="number" min={0} max={100} value={e.quality_score} onChange={(ev) => setEditing({ ...e, quality_score: Number(ev.target.value) })} style={{ width: 70 }} />
                    : s.quality_score}</td>
                  <td>{s.last_scanned_at ? new Date(s.last_scanned_at).toLocaleString() : "—"}</td>
                  <td>
                    {e ? (
                      <>
                        <button className="primary" onClick={() => onUpdate(e)}>Save</button>{" "}
                        <button onClick={() => setEditing(null)}>Cancel</button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => setEditing(s)}>Edit</button>{" "}
                        <button onClick={() => onScan(s.id)}>Scan now</button>{" "}
                        {s.source_type === "search_query" && (
                          <button onClick={() => onDryRun(s.id)} disabled={dryRunLoading === s.id}>
                            {dryRunLoading === s.id ? "Testing..." : "Dry run"}
                          </button>
                        )}{" "}
                        <button onClick={() => onShowRuns(s.id)}>Runs</button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table></div>
      )}

      {dryRun && (
        <div style={{ marginTop: "2rem" }}>
          <h3 style={{ fontFamily: "var(--font-serif)" }}>Dry run</h3>
          <p style={{ color: "var(--fg-muted)", marginTop: "0.35rem" }}>
            {dryRun.query} · {dryRun.region?.toUpperCase() ?? "GLOBAL"} · {dryRun.wouldEnqueue}/{dryRun.count} would enqueue
          </p>
          <div className="admin-table-wrap"><table className="admin-table" style={{ marginTop: "0.75rem" }}>
            <thead><tr><th>Result</th><th>Verdict</th><th>Reason</th><th>Action</th></tr></thead>
            <tbody>
              {dryRun.items.map((item) => (
                <tr key={`${item.url}-${item.title}`}>
                  <td>
                    <SafeExternalLink url={item.filteredUrl ?? item.url} label={shorten(item.title, 80)} />
                    <div className="admin-source-meta">{shorten(item.filteredUrl ?? item.url, 80)}</div>
                  </td>
                  <td>{item.decision.verdict} · {item.decision.confidence}</td>
                  <td>{item.decision.reason}</td>
                  <td>{item.wouldEnqueue ? "candidate" : "skip"}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </div>
      )}

      {runs && (
        <div style={{ marginTop: "2rem" }}>
          <h3 style={{ fontFamily: "var(--font-serif)" }}>Recent runs</h3>
          <div className="admin-table-wrap"><table className="admin-table" style={{ marginTop: "0.75rem" }}>
            <thead><tr><th>Status</th><th>Results</th><th>Accepted</th><th>New</th><th>Rejected</th><th>Started</th><th>Finished</th><th>Error</th></tr></thead>
            <tbody>
              {runs.length === 0 && <tr><td colSpan={8} style={{ textAlign: "center", padding: "1rem", color: "var(--fg-subtle)" }}>No runs yet.</td></tr>}
              {runs.map((r) => (
                <tr key={r.id}>
                  <td>{r.status}</td>
                  <td>{r.results_total ?? r.candidates_found}</td>
                  <td>{r.candidates_found}</td>
                  <td>{r.candidates_new}</td>
                  <td>{r.candidates_rejected ?? 0}</td>
                  <td>{new Date(r.started_at).toLocaleString()}</td>
                  <td>{r.finished_at ? new Date(r.finished_at).toLocaleString() : "—"}</td>
                  <td style={{ color: "var(--danger)" }}>{r.error_message ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </div>
      )}
    </div>
  );
}

function SafeExternalLink({ url, label }: { url: string | null | undefined; label: string }) {
  const safe = safeHttpUrl(url);
  return safe ? <a href={safe} target="_blank" rel="noreferrer">{label}</a> : <span>{label}</span>;
}

function shorten(s: string, n = 60): string {
  return s.length > n ? s.slice(0, n) + "…" : s;
}
